import { useEffect, useMemo } from "react"
import parse from "html-react-parser"

import { useConsole } from "contexts/Console"
import { usePage } from "contexts/Page"
import { useDictionary } from "contexts/Dictionary"

import ReadingInfo from "components/reading-info/ReadingInfo"
import { useAggregator } from "components/aggregator/Context"
import TextBlock from "components/coveroller/TextBlock"
import Roller from "components/coveroller/Roller"
import {
  Root,
  MediaContainer,
  VideoItem,
  kickerCN,
  subtitleCN,
  Hgroup,
  ImageItem,
  Title,
  Overlay,
  itemsVariants,
  variants,
} from "components/coveroller/Coveroller"
import DynaCard from "./DynaCard"
import { buildHref } from "components/scrolling-grid-aggregator/ScrollingGrid"

function buildAsset(o) {
  if (!o) return []
  const {
    cover_asset_0_public_id,
    cover_asset_0_duration,
    cover_asset_0_width,
    cover_asset_0_height,
    cover_asset_1_public_id,
    cover_asset_1_duration,
    cover_asset_1_width,
    cover_asset_1_height,
  } = o

  return [
    {
      resource_type: cover_asset_0_duration ? "video" : "image",
      public_id: cover_asset_0_public_id,
      width: cover_asset_0_width,
      height: cover_asset_0_height,
    },
    {
      resource_type: cover_asset_1_duration ? "video" : "image",
      public_id: cover_asset_1_public_id,
      width: cover_asset_1_width,
      height: cover_asset_1_height,
    },
  ].filter(v => v.public_id)
}

function sortFormat(a, b) {
  return b.width / b.height - a.width / a.height
}

export default function CoverollerDyna({
  media,
  chapo,
  cta,
  heading,
  paragraph_blocks,
  roller_title,
  meta,
  sub_heading,
  sub_heading_color,
  taxonomies,
  overlay,
  ...props
}) {
  const console = useConsole()
  const dictionary = useDictionary()
  const { is_reading_info, reading_info } = usePage()

  const { data, dyna_selection } = useAggregator()

  const taxo = taxonomies.find(v => v.taxonomy_uid === "aggregator") ?? {}
  let documentType
  switch (taxo.term_uid) {
    case "planet":
      documentType = "edito"
      break
    default:
      documentType = "event"
      break
  }

  const entries = useMemo(() => {
    return (
      data?.[1]
        ?.filter(v => v.raw.documenttype === documentType)
        ?.filter(v => v.raw.parent_page?.toLowerCase() !== "true")
        ?.filter(v => !v.isTopResult)
        ?.slice(0, 5) || null
    )
  }, [data, documentType])
  const topResult = useMemo(() => data?.[1]?.find(v => v.isTopResult) || null, [data])

  let ass = topResult?.raw
    ? buildAsset(topResult.raw)
        .map(v => {
          return +v.width >= +v.height
            ? v
            : Object.assign(v, {
                metadata: {
                  v7_type: ["portrait"],
                },
              })
        })
        .sort(sortFormat)
    : null

  // console.verbode("• top", topResult)

  const topContent = topResult
    ? {
        kicker: "",
        title: topResult?.raw.name,
        subtitle: "",
        paragraph_blocks: topResult?.raw.description ? { block: [{ paragraph: topResult.raw.description }] } : { block: [] },
        start_date: topResult?.raw.start_date,
        end_date: topResult?.raw.end_date,
        link: {
          href: topResult?.raw ? buildHref(topResult.raw) : "",
          aria_label: topResult?.raw.link_aria_label || dictionary.discoverMore(),
          label: topResult?.raw.link_label || dictionary.discoverMore(),
          icon: "none",
          style: "filled translucent-dark",
        },
      }
    : null

  if (ass?.length) {
    const isAssVideo = ass.some(v => v.resource_type === "video")
    const filtered = ass.filter(v => v.resource_type === (isAssVideo ? "video" : "image"))
    ass = filtered
      .filter(v => v.metadata)
      .slice(0, 1)
      .concat(filtered.filter(v => !v.metadata).slice(0, 1))
  }

  const isVideo = ass?.some(v => v.resource_type === "video")

  useEffect(() => {
    if (entries) {
      dyna_selection.set(entries.map(v => v?.raw?.slug).join(","))
    }
  }, [entries, dyna_selection])

  return (
    <>
      <Root variants={variants} initial='hide' animate='show'>
        <MediaContainer>
          {ass?.length ? (
            isVideo ? (
              <VideoItem video={{ sources: ass }} />
            ) : (
              <ImageItem image={{ sources: ass }} sizes='auto' />
            )
          ) : media?.[0]?.video_cld || media?.[1]?.video_cld ? (
            <VideoItem video={media[0]?.video_cld ?? media[1]?.video_cld} />
          ) : media?.[0]?.image_cld || media?.[1]?.image_cld ? (
            <ImageItem image={media[0]?.image_cld ?? media[1]?.image_cld} />
          ) : null}
        </MediaContainer>
        {overlay?.active ? <Overlay style={{ "--degcolo": overlay?.color, "--degopa": overlay?.opacity }} /> : null}
        {(heading?.kicker || heading?.subtitle) && heading?.title ? (
          <Hgroup variants={itemsVariants}>
            {heading?.kicker && <span className={kickerCN}>{parse(heading.kicker)}</span>}
            <Title as={meta.index === 0 ? "h1" : "h2"}>{parse(heading.title)}</Title>
            {heading?.subtitle && <p className={subtitleCN}>{parse(heading.subtitle)}</p>}
          </Hgroup>
        ) : heading?.title ? (
          <Title as={meta.index === 0 ? "h1" : "h2"} variants={itemsVariants}>
            {parse(heading.title)}
          </Title>
        ) : null}
        <TextBlock
          topContent={topContent}
          chapo={chapo}
          variants={itemsVariants}
          sub_heading={sub_heading}
          sub_heading_color={sub_heading_color}
          paragraph_blocks={paragraph_blocks}
          cta={cta}
        />
        {entries?.length ? (
          <Roller roller_title={roller_title} items={entries} variants={itemsVariants}>
            {entries.map(({ raw }, i) => {
              return raw ? (
                <li key={`${raw.cs_uid}-${i}`}>
                  <DynaCard {...raw} index={i} />
                </li>
              ) : null
            })}
          </Roller>
        ) : null}
      </Root>
      {is_reading_info && meta.index === 0 ? <ReadingInfo {...reading_info} /> : null}
    </>
  )
}
